<template>
    <div class="connection-apps-container">
        <o-bread-crumb>
            <template v-slot:pageName>Connection Apps</template>
            <template v-slot:pageDetail>System Integration</template>
        </o-bread-crumb>

        <div v-if="validation" class="alert alert-danger" role="alert">
            <i class="fas fa-exclamation-circle me-2"></i>{{ validation }}
        </div>

        <!-- Tab Navigation -->
        <div class="connection-tabs">
            <button
                class="connection-tab"
                :class="{ 'connection-tab--active': activeTab === 'booking' }"
                @click="activeTab = 'booking'"
            >
                <i class="fas fa-plug me-1"></i>
                Booking Engine
            </button>
            <button
                class="connection-tab"
                :class="{ 'connection-tab--active': activeTab === 'custom-token' }"
                @click="activeTab = 'custom-token'"
            >
                <i class="fas fa-key me-1"></i>
                Custom Access Tokens
            </button>
        </div>

        <div v-if="activeTab === 'booking' && thisComponentShow" class="connection-apps-card">
            <!-- Header Section -->
            <div class="connection-apps-header">
                <div class="header-left">
                    <h4 class="header-title">
                        <i class="fas fa-plug me-2 primary-text"></i>Booking Engine Integration
                    </h4>
                    <p class="header-subtitle">Manage connections of branches to Booking Engine systems</p>
                </div>
                <div class="header-actions">
                    <button @click="handleCreateClick" :disabled="!write"
                        :class="['btn-primary-action', { 'btn-disabled': !write }]"
                        :title="!write ? 'You only have read access to this module' : 'Create new connection'">
                        <i class="fas fa-plus me-1"></i>
                        <span class="btn-text">New Connection</span>
                    </button>
                </div>
            </div>

            <!-- Content Section -->
            <div class="connection-apps-content">
                <div v-if="page.output" class="filter-bar">
                    <div class="filter-bar-left">
                        <div class="view-toggle">
                            <button @click="viewMode = 'list'" :class="['btn-view', { active: viewMode === 'list' }]"
                                title="List View">
                                <i class="fas fa-list"></i>
                            </button>
                            <button @click="viewMode = 'cards'" :class="['btn-view', { active: viewMode === 'cards' }]"
                                title="Card View">
                                <i class="fas fa-th"></i>
                            </button>
                        </div>
                        <div ref="filterContainer" class="filter-container">
                            <button @click="showFilterDropdown = !showFilterDropdown" class="btn-filter-dropdown">
                                <i class="fas fa-filter me-2"></i>
                                <span>Filters</span>
                                <i class="fas fa-chevron-down ms-2"></i>
                            </button>
                            <div v-if="showFilterDropdown" class="filter-dropdown">
                                <div class="filter-dropdown-header">
                                    <span>Filters</span>
                                    <button @click="showFilterDropdown = false" class="btn-close-dropdown">
                                        <i class="fas fa-times"></i>
                                    </button>
                                </div>

                                <!-- Filters Content: Side by Side Layout -->
                                <div class="filter-sections-container">
                                    <div class="filter-section">
                                        <div class="filter-section-title">by Status</div>
                                        <div class="filter-options">
                                            <label class="filter-option">
                                                <input type="radio" name="filterStatus" value="all"
                                                    v-model="filterStatus" />
                                                <span>All</span>
                                            </label>
                                            <label class="filter-option">
                                                <input type="radio" name="filterStatus" value="connected"
                                                    v-model="filterStatus" />
                                                <span>Connected</span>
                                            </label>
                                            <label class="filter-option">
                                                <input type="radio" name="filterStatus" value="not_connected"
                                                    v-model="filterStatus" />
                                                <span>Not Connected</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div class="filter-vertical-separator"></div>

                                    <div class="filter-section">
                                        <div class="filter-section-title">by Last Updated</div>
                                        <div class="filter-options">
                                            <label class="filter-option">
                                                <input type="radio" name="filterLastUpdated" value="all"
                                                    v-model="lastUpdatedFilter" />
                                                <span>All</span>
                                            </label>
                                            <label class="filter-option">
                                                <input type="radio" name="filterLastUpdated" value="today"
                                                    v-model="lastUpdatedFilter" />
                                                <span>Today</span>
                                            </label>
                                            <label class="filter-option">
                                                <input type="radio" name="filterLastUpdated" value="yesterday"
                                                    v-model="lastUpdatedFilter" />
                                                <span>Yesterday</span>
                                            </label>
                                            <label class="filter-option">
                                                <input type="radio" name="filterLastUpdated" value="last7days"
                                                    v-model="lastUpdatedFilter" />
                                                <span>Last 7 Days</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div ref="searchContainer" class="search-container">
                            <button @click="showSearchDropdown = !showSearchDropdown" class="btn-search-dropdown">
                                <i class="fas fa-search me-2"></i>
                                <span>Search</span>
                                <i class="fas fa-chevron-down ms-2"></i>
                            </button>
                            <div v-if="showSearchDropdown" class="search-dropdown">
                                <div class="search-dropdown-header">
                                    <span>Search Connections</span>
                                    <button @click="showSearchDropdown = false" class="btn-close-dropdown">
                                        <i class="fas fa-times"></i>
                                    </button>
                                </div>
                                <div class="search-form">
                                    <select v-model="searchField" class="search-select">
                                        <option value="name">Branch Name</option>
                                        <option value="code">Branch Code</option>
                                    </select>
                                    <input v-model="searchQuery" type="text" class="search-input"
                                        placeholder="Enter search term..." @keyup.enter="performSearch" />
                                    <div class="search-actions">
                                        <button @click="performSearch" class="btn-search">
                                            <i class="fas fa-search me-1"></i>
                                            Search
                                        </button>
                                        <button @click="clearSearchForm" class="btn-clear">
                                            <i class="fas fa-times me-1"></i>
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <button class="btn-refresh" @click.prevent="refreshAll()" title="Refresh">
                        <i class="fas fa-sync-alt"></i>
                    </button>
                </div>

                <div v-if="page.isLoading" class="loading-state">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="loading-text">Loading connections...</p>
                </div>

                <div v-else-if="page.output">
                    <!-- Cards Grid -->
                    <div v-if="connectionsList.length === 0" class="empty-state">
                        <i class="fas fa-inbox fa-3x"></i>
                        <h5>No Branches Available</h5>
                        <p>Create branches first to set up connections</p>
                    </div>

                    <div v-else :class="viewMode === 'list' ? 'cards-list' : 'cards-grid'">
                        <connection-status-card v-for="item in connectionsList" :key="item.branch_id"
                            :view-mode="viewMode" :branch="{
                                id: item.branch_id,
                                name: item.branch_name,
                                code: item.branch_code
                            }" :connection="item.connection"
                            :connectionType="item.connection_type === 'new' ? 'New' : item.connection_type === 'legacy' ? 'Legacy' : 'None'"
                            @setup="goToSetup" @view="goToDetail" />
                    </div>

                    <!-- Pagination -->
                    <div class="pagination-wrapper" v-if="connectionsList.length > 0">
                        <o-pagination :page="meta.page" :total="meta.total" @callback="navPage" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Pre-Check Modal -->
        <o-modal v-if="showPreCheckModal" @close="showPreCheckModal = false" modal-class="show d-block">
            <template v-slot:header>
                <div class="modal-header-visual">
                    <div class="icon-circle">
                        <i class="fas fa-clipboard-check"></i>
                    </div>
                    <h5 class="modal-title-custom">Prerequisites Check</h5>
                    <p class="modal-subtitle">Before proceeding, please verify the following requirements:</p>
                </div>
            </template>
            <template v-slot:body>
                <div class="checklist-container">
                    <div class="checklist-item">
                        <div class="check-icon"><i class="fas fa-check"></i></div>
                        <div class="check-content">
                            <strong>System Property Mapping</strong>
                            <p>Confirm that the branch exists in your system property mapping configuration.</p>
                        </div>
                    </div>
                    <div class="checklist-item">
                        <div class="check-icon"><i class="fas fa-check"></i></div>
                        <div class="check-content">
                            <strong>Connection Readiness</strong>
                            <p>Confirm that your endpoint/system is ready to accept the connection.</p>
                        </div>
                    </div>
                </div>

                <div class="confirmation-box">
                    <label class="custom-checkbox-container">
                        <input type="checkbox" id="preCheckConfirm" v-model="preCheckConfirmed">
                        <span class="checkmark"></span>
                        <span class="label-text">I have read and confirmed the requirements above</span>
                    </label>
                </div>

                <div class="action-footer">
                    <button class="btn-create-connection" :disabled="!preCheckConfirmed" @click="proceedToCreate">
                        Proceed to Setup
                        <i class="fas fa-arrow-right ms-2"></i>
                    </button>
                    <button class="btn-cancel-link" @click="showPreCheckModal = false">Cancel</button>
                </div>
            </template>
        </o-modal>

        <!-- Custom API Tokens Tab Content -->
        <div v-if="activeTab === 'custom-token'">
            <custom-token-list />
        </div>
    </div>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { OPagination, OBreadCrumb, OModal } from '@/components/OmnihControl'
import ConnectionStatusCard from '@/components/ConnectionApps/ConnectionStatusCard.vue'
import CustomTokenList from '@/views/ConnectionApps/CustomToken/CustomTokenList.vue'

export default {
    name: 'ConnectionAppsList',
    props: {
        // Allow parent to specify which store module to use.
        // Defaults to 'ConnectionApps' for backward compatibility.
        // Pass 'ExtranetConnectionApps' when used in Extranet context.
        storeModule: {
            type: String,
            default: 'ConnectionApps'
        }
    },
    components: {
        OPagination,
        OBreadCrumb,
        ConnectionStatusCard,
        OModal,
        CustomTokenList
    },
    setup(props) {
        const router = useRouter()
        const store = useStore()

        const page = reactive({
            isLoading: false,
            output: false,
            component: 'connection-apps'
        })

        const viewMode = ref('cards') // 'cards' or 'list'
        const activeTab = ref(router.currentRoute.value.query.tab || 'booking') // 'booking' or 'custom-token'
        const filterStatus = ref('all') // 'all', 'connected', 'not_connected'
        const showFilterDropdown = ref(false)
        const showSearchDropdown = ref(false)
        const searchField = ref('name')
        const searchQuery = ref('')
        const filterContainer = ref(null)
        const searchContainer = ref(null)
        const lastUpdatedFilter = ref('all') // 'all', 'today', 'yesterday', 'last7days'

        const updateViewMode = () => {
            if (window.innerWidth <= 1024) {
                viewMode.value = 'cards'
            }
        }

        // Handle click outside to close dropdowns
        const handleClickOutside = (event) => {
            if (filterContainer.value && !filterContainer.value.contains(event.target)) {
                showFilterDropdown.value = false
            }
            if (searchContainer.value && !searchContainer.value.contains(event.target)) {
                showSearchDropdown.value = false
            }
        }

        onMounted(() => {
            updateViewMode()
            window.addEventListener('resize', updateViewMode)
            document.addEventListener('click', handleClickOutside)
        })

        onBeforeUnmount(() => {
            window.removeEventListener('resize', updateViewMode)
            document.removeEventListener('click', handleClickOutside)
        })

        const searchOptions = reactive([
            { value: 'name', text: 'Branch Name' },
            { value: 'code', text: 'Branch Code' }
        ])

        const validation = ref('')
        const thisComponentShow = ref(true)
        const write = ref(true)

        const meta = computed(() => store.state[props.storeModule].meta)

        const connectionsList = computed(() => {
            return store.getters[`${props.storeModule}/connections`] || []
        })

        onMounted(() => {
            loadState()
        })

        watch(filterStatus, (newValue) => {
            const currentSearch = store.state[props.storeModule].search || {}
            const updatedSearch = { ...currentSearch }

            if (newValue === 'all') {
                delete updatedSearch.connection_status_filter
            } else {
                updatedSearch.connection_status_filter = newValue
            }

            store.commit(`${props.storeModule}/set_search`, { search: updatedSearch })

            const updatedMeta = { ...meta.value, page: 1 }
            store.commit(`${props.storeModule}/switch_page`, { meta: updatedMeta })

            loadState()
        })

        watch(lastUpdatedFilter, (newValue) => {
            const currentSearch = store.state[props.storeModule].search || {}
            const updatedSearch = { ...currentSearch }

            if (newValue === 'all') {
                delete updatedSearch.updated_at_filter
            } else {
                updatedSearch.updated_at_filter = newValue
            }

            store.commit(`${props.storeModule}/set_search`, { search: updatedSearch })

            const updatedMeta = { ...meta.value, page: 1 }
            store.commit(`${props.storeModule}/switch_page`, { meta: updatedMeta })

            loadState()
        })

        function loadState() {
            page.isLoading = true
            validation.value = ''

            store.dispatch(`${props.storeModule}/read`)
                .then(() => {
                    page.output = true
                })
                .catch((err) => {
                    const errorMessage = err.response?.data?.message || err.message || 'Failed to load connections'
                    validation.value = errorMessage
                    console.error('Error loading connections:', err)
                })
                .finally(() => {
                    page.isLoading = false
                })
        }

        function refreshAll() {
            filterStatus.value = 'all'
            lastUpdatedFilter.value = 'all'
            searchQuery.value = ''
            searchField.value = 'name'
            store.commit(`${props.storeModule}/clear_search`)
            showFilterDropdown.value = false
            showSearchDropdown.value = false
            loadState()
        }


        function getConnectionForBranch(branchId) {
            const item = connectionsList.value.find(conn => conn.branch_id === branchId)
            return item?.connection || null
        }

        function getConnectionType(branchId) {
            const item = connectionsList.value.find(conn => conn.branch_id === branchId)
            return item?.connection_type || 'none'
        }

        const showPreCheckModal = ref(false)
        const preCheckConfirmed = ref(false)

        function handleCreateClick() {
            if (!write.value) {
                validation.value = 'You only have read access to this module. Write permission is required to create connection.'
                setTimeout(() => {
                    validation.value = ''
                }, 5000)
                return
            }
            showPreCheckModal.value = true
            preCheckConfirmed.value = false
        }

        function proceedToCreate() {
            if (preCheckConfirmed.value) {
                showPreCheckModal.value = false
                goToSetup(null, 'new')
            }
        }

        function goToSetup(branch = null, mode = null) {
            const query = {}
            if (branch) query.branchId = branch.id
            if (mode) query.mode = mode

            router.push({
                name: 'ConnectionSetup',
                query
            })
        }

        function goToDetail(connection) {
            router.push({ name: 'ConnectionDetail', params: { id: connection.id } })
        }

        function clearSearch() {
            store.commit(`${props.storeModule}/clear_search`)
            loadState()
        }

        function performSearch() {
            if (!searchQuery.value.trim()) {
                return
            }

            const searchObj = {}
            searchObj[searchField.value] = searchQuery.value.trim()
            store.commit(`${props.storeModule}/set_search`, { search: searchObj })

            const updatedMeta = { ...meta.value, page: 1 }
            store.commit(`${props.storeModule}/switch_page`, { meta: updatedMeta })
            loadState()
        }

        function clearSearchForm() {
            searchQuery.value = ''
            searchField.value = 'name'
            clearSearch()
            showSearchDropdown.value = false
        }

        function navPage(pageNum) {
            const updatedMeta = { ...meta.value, page: pageNum }
            store.commit(`${props.storeModule}/switch_page`, { meta: updatedMeta })
            loadState()
        }

        return {
            page,
            searchOptions,
            validation,
            thisComponentShow,
            write,
            connectionsList,
            meta,
            viewMode,
            activeTab,
            filterStatus,
            showFilterDropdown,
            showSearchDropdown,
            searchField,
            searchQuery,
            filterContainer,
            searchContainer,
            lastUpdatedFilter,
            loadState,
            refreshAll,
            handleCreateClick,
            proceedToCreate,
            showPreCheckModal,
            preCheckConfirmed,
            goToSetup,
            goToDetail,
            getConnectionForBranch,
            getConnectionType,
            clearSearch,
            performSearch,
            clearSearchForm,
            navPage
        }
    }
}
</script>

<style scoped>
/* Container */
.connection-apps-container {
    padding: 16px;
    padding-right: 20px;
    max-width: 100%;
}

/* Main Card */
.connection-apps-card {
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
}

/* Header */
.connection-apps-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e5e7eb;
    gap: 1rem;
}

.header-left {
    flex: 1;
}

.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
}

.header-actions {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
}

/* Buttons */
.btn-primary-action {
    background: var(--primary-color, #3b82f6);
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.btn-primary-action:hover {
    background: var(--primary-color-dark, #2563eb);
    transform: translateY(-1px);
}

.btn-primary-action.btn-disabled,
.btn-primary-action:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    opacity: 0.6;
}

.btn-primary-action.btn-disabled:hover,
.btn-primary-action:disabled:hover {
    background: #9ca3af;
    transform: none;
}

.btn-secondary-action {
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.btn-secondary-action:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

.btn-cancel-action {
    background: #9ca3af;
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
}

.btn-cancel-action:hover {
    background: #6b7280;
}

/* Content */
.connection-apps-content {
    padding: 1.5rem 2rem;
}

/* Loading State */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
}

.loading-text {
    margin-top: 1rem;
    color: #6b7280;
    font-size: 0.875rem;
}

/* Filter Bar */
.filter-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    gap: 1rem;
}

.filter-bar-left {
    display: flex;
    align-items: center;
    gap: 1rem;
}



.search-wrapper {
    flex: 1;
    max-width: 600px;
}

/* Force o-search to display inline */
.search-wrapper :deep(.search-container),
.search-wrapper :deep(.o-search) {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    gap: 0.5rem !important;
}

.search-wrapper :deep(.search-select),
.search-wrapper :deep(select) {
    flex: 0 0 auto !important;
    min-width: 150px !important;
    max-width: 200px !important;
}

.search-wrapper :deep(.search-input),
.search-wrapper :deep(input[type="text"]) {
    flex: 1 !important;
}

.search-wrapper :deep(.search-buttons),
.search-wrapper :deep(.btn-group) {
    flex: 0 0 auto !important;
    display: flex !important;
    gap: 0.5rem !important;
}

/* View Toggle */
.view-toggle {
    display: flex;
    gap: 0.25rem;
    background: #f3f4f6;
    padding: 0.25rem;
    border-radius: 8px;
}

.btn-view {
    background: transparent;
    color: #6b7280;
    border: none;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-view:hover {
    background: #e5e7eb;
    color: #374151;
}

.btn-view.active {
    background: white;
    color: var(--primary-color, #3b82f6);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Filter Dropdown */
.filter-container {
    position: relative;
}

.btn-filter-dropdown {
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.875rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.btn-filter-dropdown:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

.filter-dropdown {
    position: absolute;
    top: calc(100% + 0.5rem);
    left: 0;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    z-index: 50;
    min-width: 500px;
}

/* Filter Sections Container: Side by Side Layout */
.filter-sections-container {
    display: flex;
    gap: 0;
}

.filter-dropdown-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;
    font-weight: 600;
    color: #111827;
    font-size: 0.875rem;
}

.btn-close-dropdown {
    background: transparent;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;
}

.btn-close-dropdown:hover {
    background: #f3f4f6;
    color: #374151;
}

.filter-options {
    padding: 0.5rem;
}

.filter-option {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 0.75rem;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.2s ease;
    font-size: 0.875rem;
    color: #374151;
}

.filter-option:hover {
    background: #f9fafb;
}

.filter-option input[type="radio"] {
    margin: 0;
    cursor: pointer;
    accent-color: var(--primary-color, #3b82f6);
    width: 14px;
    height: 14px;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    border: 1px solid #d1d5db;
    border-radius: 50%;
    outline: none;
    transition: all 0.2s ease;
    position: relative;
}

.filter-option input[type="radio"]:checked {
    border-color: var(--primary-color, #3b82f6);
    background-color: white;
}

.filter-option input[type="radio"]:checked::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--primary-color, #3b82f6);
}

.filter-option input[type="radio"]:hover {
    border-color: var(--primary-color, #3b82f6);
}

.filter-option span {
    flex: 1;
}

/* Filter Sections */
.filter-section {
    padding: 0.5rem;
    flex: 1;
    min-width: 0;
}

.filter-section-title {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.5rem 0.75rem 0.25rem;
}

.filter-separator {
    height: 1px;
    background: #e5e7eb;
    margin: 0.5rem 0;
}

.filter-vertical-separator {
    width: 1px;
    background: #e5e7eb;
    margin: 0.5rem 0;
}

/* Search Dropdown */
.search-container {
    position: relative;
    flex: 1;
    max-width: 600px;
}

.btn-search-dropdown {
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.875rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.btn-search-dropdown:hover {
    background: #f9fafb;
    border-color: #9ca3af;
}

.search-dropdown {
    position: absolute;
    top: calc(100% + 0.5rem);
    left: 0;
    right: 0;
    min-width: 350px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    z-index: 50;
}

.search-dropdown-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;
    font-weight: 600;
    color: #111827;
    font-size: 0.875rem;
}

.search-form {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.search-select {
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    color: #374151;
    background: white;
    cursor: pointer;
}

.search-select:focus {
    outline: none;
    border-color: var(--primary-color, #3b82f6);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-input {
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    color: #374151;
}

.search-input:focus {
    outline: none;
    border-color: var(--primary-color, #3b82f6);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-input::placeholder {
    color: #9ca3af;
}

.search-actions {
    display: flex;
    gap: 0.5rem;
}

.btn-search {
    flex: 1;
    background: var(--primary-color, #3b82f6);
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-search:hover {
    background: var(--primary-color-dark, #2563eb);
}

.btn-clear {
    background: #6b7280;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-clear:hover {
    background: #4b5563;
}

.btn-refresh {
    background: white;
    color: var(--primary-color, #3b82f6);
    border: 1px solid #e5e7eb;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-refresh:hover {
    background: #f9fafb;
    border-color: var(--primary-color, #3b82f6);
}

/* Cards Grid */
.cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 1.5rem;
    margin-bottom: 1.5rem;
}

/* Cards List (for list view) */
.cards-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
}


/* Empty State */
.empty-state {
    text-align: center;
    padding: 4rem 2rem;
    color: #9ca3af;
}

.empty-state i {
    margin-bottom: 1rem;
    opacity: 0.5;
}

.empty-state h5 {
    color: #374151;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.empty-state p {
    color: #6b7280;
    font-size: 0.875rem;
}

/* Pagination */
.pagination-wrapper {
    display: flex;
    justify-content: center;
    padding-top: 1rem;
}

/* Alert */
.alert {
    margin-bottom: 1rem;
    padding: 1rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
}

.alert-danger {
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
}

/* Responsive */
@media (max-width: 1024px) {
    .view-toggle {
        display: none;
    }
}

/* Pre-Check Modal Styles */
.modal-header-visual {
    text-align: center;
    padding: 1rem 0;
}

.icon-circle {
    width: 60px;
    height: 60px;
    background: var(--secondary-color, #eff6ff);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
}

.icon-circle i {
    font-size: 1.75rem;
    color: var(--primary-color, #3b82f6);
}

.modal-title-custom {
    font-size: 1.25rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0.5rem;
}

.modal-subtitle {
    color: #6b7280;
    font-size: 0.95rem;
    max-width: 80%;
    margin: 0 auto;
}

.checklist-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.checklist-item {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    background: #f9fafb;
    border: 1px solid #f3f4f6;
    border-radius: 10px;
}

.check-icon {
    width: 24px;
    height: 24px;
    background: var(--primary-color, #10b981);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
}

.check-icon i {
    color: white;
    font-size: 0.75rem;
}

.check-content strong {
    display: block;
    color: #374151;
    margin-bottom: 0.25rem;
    font-size: 0.95rem;
}

.check-content p {
    margin: 0;
    color: #6b7280;
    font-size: 0.85rem;
    line-height: 1.4;
}

.confirmation-box {
    background: var(--secondary-color, #eff6ff);
    border: 1px dashed var(--secondary-color, #3b82f6);
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1.5rem;
}

.custom-checkbox-container {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 0.9rem;
    user-select: none;
    margin: 0;
}

.custom-checkbox-container input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
}

.custom-checkbox-container .checkmark {
    height: 20px;
    width: 20px;
    background-color: white;
    border: 2px solid #d1d5db;
    border-radius: 4px;
    margin-right: 12px;
    position: relative;
    transition: all 0.2s;
    flex-shrink: 0;
}

.custom-checkbox-container:hover input ~ .checkmark {
    border-color: var(--primary-color, #3b82f6);
}

.custom-checkbox-container input:checked ~ .checkmark {
    background-color: var(--primary-color, #3b82f6);
    border-color: var(--primary-color, #3b82f6);
}

.custom-checkbox-container .checkmark:after {
    content: "";
    position: absolute;
    display: none;
}

.custom-checkbox-container input:checked ~ .checkmark:after {
    display: block;
}

.custom-checkbox-container .checkmark:after {
    left: 6px;
    top: 2px;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.custom-checkbox-container .label-text {
    color: #4b5563;
    font-weight: 500;
}

.action-footer {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.btn-create-connection {
    width: 100%;
    background: var(--primary-color, #3b82f6);
    color: white;
    border: none;
    padding: 1rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.2);
}

.btn-create-connection:hover:not(:disabled) {
    background: var(--primary-color, #2563eb);
    transform: translateY(-2px);
    box-shadow: 0 6px 8px -1px var(--primary-color, rgba(59, 130, 246, 0.3));
}

.btn-create-connection:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
}

.btn-cancel-link {
    background: transparent;
    border: none;
    color: #6b7280;
    font-size: 0.9rem;
    cursor: pointer;
    padding: 0.5rem;
}

.btn-cancel-link:hover {
    color: #374151;
    text-decoration: underline;
}

/* Tab Navigation */
.connection-tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 24px;
    background: #F5F6FA;
    padding: 4px;
    border-radius: 12px;
    width: fit-content;
}

.connection-tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: #717784;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.connection-tab:hover {
    color: #374151;
}

.connection-tab--active {
    background: #FFFFFF;
    color: var(--primary-color, #F93E3E);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    font-weight: 600;
}
</style>
